import React, { useEffect } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Radio } from '@atlaskit/radio';
import { FormValues, ChampionPreference } from '../../../types/ChampionApplicationFormTypes';
import { FieldHelpTooltip } from '../FieldHelpTooltip';

type FieldErrors = {
  championPreference?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  accountIsManaged?: boolean | null;
  errors?: FieldErrors;
};

const PREFERENCE_OPTIONS: {
  id: NonNullable<ChampionPreference>;
  label: string;
  group: 'community' | 'success';
}[] = [
  {
    id: 'community_forums_contributor',
    label: 'Contributing to Atlassian Community Forums',
    group: 'community',
  },
  {
    id: 'community_event_organizer',
    label: 'Organizing Atlassian Community Events',
    group: 'community',
  },
  {
    id: 'community_content_creator',
    label: 'Creating original thought leadership or learning content for social media',
    group: 'community',
  },
  {
    id: 'success_training_builder',
    label: 'Creating or delivering Atlassian training for your organization\u2019s end users',
    group: 'success',
  },
  {
    id: 'success_cug_leader',
    label: 'Running Atlassian-focused training events or a Company User Group in your organization',
    group: 'success',
  },
  {
    id: 'success_coe_contributor',
    label: 'Contributing Atlassian knowledge to a Center of Excellence in your organization',
    group: 'success',
  },
];

const SUCCESS_OPTION_IDS: ChampionPreference[] = [
  'success_training_builder',
  'success_cug_leader',
  'success_coe_contributor',
];

export const PreferenceSection: React.FC<Props> = ({
  values,
  updateField,
  accountIsManaged,
  errors,
}) => {
  const selected = values.championPreference;

  const handleChange = (id: ChampionPreference) => () => updateField('championPreference', id);

  const communityOptions = PREFERENCE_OPTIONS.filter((o) => o.group === 'community');
  const successOptions = PREFERENCE_OPTIONS.filter((o) => o.group === 'success');

  const isSuccessDisabled = accountIsManaged === false || accountIsManaged === null;

  useEffect(() => {
    if (isSuccessDisabled && selected && SUCCESS_OPTION_IDS.includes(selected)) {
      updateField('championPreference', null);
    }
  }, [isSuccessDisabled, selected, updateField]);

  return (
    <div css={sectionBoxStyles} className="champion-form-preference">
      <div css={headingLabelStyles} className="champion-form-preference-heading">
        How do you want to contribute as an Atlassian Champion? Select your top preference
      </div>

      <div css={controlTopStyles}>
        <p css={groupTitleStyles}>
          Community Champion
          <FieldHelpTooltip
            content="Your selected contribution type will be used to assess your eligibility and ongoing champion status. But you can still contribute to the community in multiple ways."
            iconLabel="Community Champion Help Info"
          />
        </p>
        {communityOptions.map((opt) => (
          <Radio
            key={opt.id}
            name="champion_preference"
            label={opt.label}
            value={opt.id}
            isChecked={selected === opt.id}
            onChange={handleChange(opt.id)}
          />
        ))}

        {isSuccessDisabled && (
          <div css={successMessageStyles} className="champion-form-success-message">
            <span css={successMessageIconStyles} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" focusable="false">
                <path
                  fill="currentColor"
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
                />
              </svg>
            </span>
            <span css={successMessageTextStyles}>
              Switch to an Atlassian account that uses an organization email to apply to be a Success
              Champion.
            </span>
          </div>
        )}

        <div
          className={isSuccessDisabled ? 'champion-form-success-washed-out' : undefined}
          css={successBlockStyles}
        >
          <p css={groupTitleTopStyles}>
            Success Champion (join waitlist)
            <FieldHelpTooltip
              content="The Success Champion program is in Beta. Eligibility and participation are currently limited. By selecting this track, you'll join our waitlist."
              iconLabel="Success Champion Help Info"
            />
          </p>
          {successOptions.map((opt) => (
            <Radio
              key={opt.id}
              name="champion_preference"
              label={opt.label}
              value={opt.id}
              isChecked={selected === opt.id}
              onChange={handleChange(opt.id)}
              isDisabled={isSuccessDisabled}
            />
          ))}
        </div>
      </div>

      {errors?.championPreference && <div css={errorStyles}>{errors.championPreference}</div>}
    </div>
  );
};

const sectionBoxStyles = css({
  marginBottom: '1rem',
});

const headingLabelStyles = css({
  fontWeight: 600,
  fontSize: '10pt',
  fontFamily: 'sans-serif',
  color: 'black',
  lineHeight: 1.5,
});

const controlTopStyles = css({
  marginTop: token('space.050'),
});

const groupTitleStyles = css({
  fontWeight: 400,
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  marginBottom: '0.5em', /* half line between heading and options (max one line) */
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
});

const successBlockStyles = css({
  display: 'block',
  paddingTop: '2rem', /* clear separation from Community block (halved from 4rem) */
});

const successMessageStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  maxWidth: '692px',
  minHeight: '24px',
  marginTop: '1rem',
  marginBottom: '1rem',
});

const successMessageIconStyles = css({
  display: 'inline-flex',
  flexShrink: 0,
  color: 'var(--champion-form-purple)',
  '& svg': {
    width: '20px',
    height: '20px',
  },
});

const successMessageTextStyles = css({
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  lineHeight: 1.5,
});

const groupTitleTopStyles = css({
  fontWeight: 400,
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  marginBottom: '0.5em', /* half line between heading and options (max one line) */
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
